import {
  sanitizeLocationEntries,
  reconstructEntriesFromLegacy,
  deriveLegacyLocationFields,
} from "./locationEntries";

const GOOGLE_PLACES_THEME_STYLE_ID = "kbean-google-places-theme-style";
export const AVATAR_CROP_MIN_ZOOM = 0.5;
export const AVATAR_CROP_MAX_ZOOM = 3;

const normalizeAvatarCropZoom = (zoom) =>
  Math.max(AVATAR_CROP_MIN_ZOOM, Math.min(AVATAR_CROP_MAX_ZOOM, Number(zoom) || 1));

const toDraftBusinessName = (businessId = "") => {
  const shortId = String(businessId || "").trim().slice(0, 6).toUpperCase();
  return shortId ? `Draft Business ${shortId}` : "Draft Business";
};

export const resolveBusinessDisplayName = (business = {}) => {
  const companyName = String(business?.companyName || "").trim();
  if (companyName) return companyName;
  const explicitDisplayName = String(business?.displayName || "").trim();
  if (explicitDisplayName) return explicitDisplayName;
  return toDraftBusinessName(business?.id);
};

export const normalizeBusinessMembershipRole = (role) => {
  const normalized = String(role || "").trim().toLowerCase();
  if (!normalized) return "";
  if (normalized === "owner") return "owner";
  if (normalized === "manager") return "manager";
  if (normalized === "collaborator") return "collaborator";
  return "";
};

export const getBusinessRolePermissions = (role) => {
  const normalizedRole = normalizeBusinessMembershipRole(role);
  return {
    role: normalizedRole,
    canEditBusiness: normalizedRole === "owner" || normalizedRole === "manager",
    canDeleteBusiness: normalizedRole === "owner",
    canManageTeam: normalizedRole === "owner" || normalizedRole === "manager",
    canLeaveBusiness: Boolean(normalizedRole) && normalizedRole !== "owner",
  };
};

export const resolveNextPrimaryBusinessIdAfterDraftCancel = ({
  draftBusinessId = "",
  currentPrimaryBusinessId = "",
  businesses = [],
} = {}) => {
  const normalizedDraftId = String(draftBusinessId || "").trim();
  const normalizedCurrentId = String(currentPrimaryBusinessId || "").trim();
  if (!normalizedDraftId) return normalizedCurrentId || null;
  if (normalizedCurrentId && normalizedCurrentId !== normalizedDraftId) {
    return normalizedCurrentId;
  }
  const fallback = (Array.isArray(businesses) ? businesses : []).find(
    (business) => String(business?.id || "").trim() !== normalizedDraftId,
  );
  return String(fallback?.id || "").trim() || null;
};

export const defaultScopesForRole = (role) => {
  switch (normalizeBusinessMembershipRole(role)) {
    case "owner":
    case "manager":
    case "collaborator":
      return { tv: true, timeCapsules: true };
    case "tv_manager":
      return { tv: true, timeCapsules: false };
    case "timecapsules_manager":
      return { tv: false, timeCapsules: true };
    default:
      return { tv: true, timeCapsules: false };
  }
};

export const hasAtLeastOneBusinessScope = (scopes = {}) =>
  Boolean(scopes?.tv) || Boolean(scopes?.timeCapsules);

// Shared MUI Switch styling used across the Business Hub (the panel's Live
// toggle and the invite dialog's scope toggles): a 42x24 pill track with a
// contained 20px white knob, green when checked.
export const buildTemplateSwitchSx = (isLight = true) => ({
  width: 42,
  height: 24,
  padding: 0,
  "& .MuiSwitch-switchBase": {
    padding: 0,
    margin: "2px",
    transitionDuration: "150ms",
    "&.Mui-checked": {
      transform: "translateX(18px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        backgroundColor: "#16A34A",
        opacity: 1,
        border: 0,
      },
    },
    "&.Mui-disabled + .MuiSwitch-track": { opacity: 0.5 },
  },
  "& .MuiSwitch-thumb": {
    boxSizing: "border-box",
    width: 20,
    height: 20,
    boxShadow: "0 1px 2px rgba(0,0,0,0.25)",
  },
  "& .MuiSwitch-track": {
    borderRadius: 13,
    backgroundColor: isLight ? "#D4D4D8" : "rgba(255,255,255,0.22)",
    opacity: 1,
  },
});

export const formatBusinessProfileStatus = (status) => {
  const normalized = String(status || "").trim().toLowerCase();
  if (normalized === "active") return "Business live";
  if (normalized === "inactive") return "Business inactive";
  if (normalized === "draft") return "Business in draft";
  if (normalized === "deleted") return "Business deleted";
  return normalized ? `Profile ${normalized}` : "Business unknown";
};

export const getBusinessActivationChecks = (businessLike = {}) => {
  const companyName = String(businessLike?.companyName || "").trim();
  const hqAddress = String(businessLike?.hqAddress || "").trim();
  const businessCategories = Array.isArray(businessLike?.businessCategories)
    ? businessLike.businessCategories
    : [];
  const approxEmployeeCount = String(businessLike?.approxEmployeeCount || "").trim();
  const avatarUrl = String(businessLike?.avatarUrl || "").trim();
  const pinUrl = String(businessLike?.pinUrl || "").trim();
  const adRestrictionsEnabled =
    businessLike?.adRestrictionsEnabled === undefined
      ? (businessLike?.adRestrictions?.enabled === undefined
          ? false
          : Boolean(businessLike?.adRestrictions?.enabled))
      : Boolean(businessLike?.adRestrictionsEnabled);
  const adRestrictionCategories = Array.isArray(businessLike?.adRestrictionCategories)
    ? businessLike.adRestrictionCategories
    : Array.isArray(businessLike?.adRestrictions?.categories)
      ? businessLike.adRestrictions.categories
      : [];
  const adBlockedKeywords = Array.isArray(businessLike?.adBlockedKeywords)
    ? businessLike.adBlockedKeywords
    : Array.isArray(businessLike?.adRestrictions?.blockedKeywords)
      ? businessLike.adRestrictions.blockedKeywords
      : [];

  return [
    {
      key: "company_name",
      label: "Company name added",
      complete: Boolean(companyName),
    },
    {
      key: "hq_address",
      label: "HQ address added",
      complete: Boolean(hqAddress),
    },
    {
      key: "categories",
      label: "At least one business category selected",
      complete: businessCategories.length > 0,
    },
    {
      key: "employee_count",
      label: "Approximate employee count selected",
      complete: Boolean(approxEmployeeCount),
    },
    {
      key: "business_logo",
      label: "Business logo & map pin added",
      complete: Boolean(avatarUrl) && Boolean(pinUrl),
    },
    {
      key: "ad_restrictions",
      label: "Ad restrictions configured",
      complete:
        !adRestrictionsEnabled ||
        adRestrictionCategories.length > 0 ||
        adBlockedKeywords.length > 0,
    },
  ];
};

export const isBusinessActivationReady = (businessLike = {}) =>
  getBusinessActivationChecks(businessLike).every((check) => check.complete);

const normalizeLocationText = (value) => String(value || "").replace(/\s+/g, " ").trim();

export const normalizeBusinessWebsiteUrl = (value) => {
  const trimmed = String(value || "").trim();
  if (!trimmed) return "";
  const withProtocol = /^https?:\/\//i.test(trimmed)
    ? trimmed.replace(/^http:\/\//i, "https://")
    : `https://${trimmed}`;

  try {
    const parsed = new URL(withProtocol);
    parsed.protocol = "https:";
    return parsed.toString().replace(/\/$/, "");
  } catch {
    return withProtocol;
  }
};

export const sanitizeBusinessLocations = (locations = [], hqAddress = "") => {
  const normalizedHqAddress = normalizeLocationText(hqAddress).toLowerCase();
  const unique = [];
  const seen = new Set();

  (Array.isArray(locations) ? locations : []).forEach((value) => {
    const normalized = normalizeLocationText(value);
    if (!normalized) return;
    const key = normalized.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    unique.push(normalized);
  });

  if (!normalizedHqAddress) return unique;

  const hasExactHq = unique.some((value) => value.toLowerCase() === normalizedHqAddress);
  if (!hasExactHq) return unique;

  return unique.filter((value) => {
    const normalized = value.toLowerCase();
    if (normalized === normalizedHqAddress) return true;
    const isLikelyTypingPrefix = normalizedHqAddress.startsWith(normalized);
    if (!isLikelyTypingPrefix) return true;
    return normalized.length >= Math.max(12, normalizedHqAddress.length - 6);
  });
};

export const sanitizeBusinessLocationDetails = (details = [], hqAddress = "", locations = []) => {
  const validAddresses = new Set(
    sanitizeBusinessLocations(
      [
        hqAddress,
        ...(Array.isArray(locations) ? locations : []),
      ],
      "",
    ).map((value) => value.toLowerCase()),
  );
  const rows = [];
  const seen = new Set();
  (Array.isArray(details) ? details : []).forEach((entry) => {
    const address = normalizeLocationText(entry?.address);
    const key = address.toLowerCase();
    if (!address || seen.has(key)) return;
    if (validAddresses.size > 0 && !validAddresses.has(key)) return;
    seen.add(key);
    const lat = Number(entry?.lat);
    const lng = Number(entry?.lng);
    rows.push({
      address,
      placeId: String(entry?.placeId || "").trim() || null,
      addressComponents: Array.isArray(entry?.addressComponents)
        ? entry.addressComponents
        : [],
      ...(Number.isFinite(lat) && Number.isFinite(lng)
        ? {
            lat,
            lng,
            geohash: String(entry?.geohash || "").trim() || null,
          }
        : {}),
    });
  });
  return rows;
};

export const mapBusinessToFormData = (business = {}) => {
  const sanitizedLocations = sanitizeBusinessLocations(
    business.locations,
    business.hqAddress,
  );
  const sanitizedLocationDetails = sanitizeBusinessLocationDetails(
    business.locationDetails,
    business.hqAddress,
    business.locations,
  );
  const locationEntries =
    Array.isArray(business.locationEntries) && business.locationEntries.length
      ? sanitizeLocationEntries(business.locationEntries)
      : reconstructEntriesFromLegacy({
          hqAddress: business.hqAddress,
          hqPlaceId: business.hqPlaceId,
          hqAddressComponents: business.hqAddressComponents,
          hqLocation: business.hqLocation,
          locations: sanitizedLocations,
          locationDetails: sanitizedLocationDetails,
        });
  return {
  companyName: business.companyName || "",
  tagline: business.tagline || "",
  corporationId: business.corporationId || "",
  businessCategories: Array.isArray(business.businessCategories)
    ? business.businessCategories
    : [],
  hqAddress: business.hqAddress || "",
  hqPlaceId: business.hqPlaceId || "",
  hqAddressComponents: Array.isArray(business.hqAddressComponents)
    ? business.hqAddressComponents
    : [],
  hqLocation: business.hqLocation || null,
  locations: sanitizedLocations,
  locationDetails: sanitizedLocationDetails,
  locationEntries,
  approxEmployeeCount: business.approxEmployeeCount || "",
  hashtags: Array.isArray(business.hashtags) ? business.hashtags : [],
  websiteUrl: normalizeBusinessWebsiteUrl(business.websiteUrl || business.businessWebsiteUrl || ""),
  marketingCampaignDescription: business.marketingCampaignDescription || "",
  adRestrictionsEnabled:
    business?.adRestrictions?.enabled === undefined
      ? false
      : Boolean(business?.adRestrictions?.enabled),
  adRestrictionCategories: Array.isArray(business?.adRestrictions?.categories)
    ? business.adRestrictions.categories
    : [],
  adBlockedKeywords: Array.isArray(business?.adRestrictions?.blockedKeywords)
    ? business.adRestrictions.blockedKeywords
    : [],
  avatarUrl: business.avatarUrl || "",
  avatarStoragePath: business.avatarStoragePath || "",
  pinUrl: business.pinUrl || "",
  pinStoragePath: business.pinStoragePath || "",
  };
};

export const mergeUploadedAvatarIntoFormData = (currentFormData = {}, uploadedAvatar = {}) => ({
  avatarUrl:
    uploadedAvatar?.avatarUrl === undefined
      ? String(currentFormData?.avatarUrl || "").trim()
      : String(uploadedAvatar?.avatarUrl || "").trim(),
  avatarStoragePath:
    uploadedAvatar?.avatarStoragePath === undefined
      ? String(currentFormData?.avatarStoragePath || "").trim()
      : String(uploadedAvatar?.avatarStoragePath || "").trim(),
  pinUrl:
    uploadedAvatar?.pinUrl === undefined
      ? String(currentFormData?.pinUrl || "").trim()
      : String(uploadedAvatar?.pinUrl || "").trim(),
  pinStoragePath:
    uploadedAvatar?.pinStoragePath === undefined
      ? String(currentFormData?.pinStoragePath || "").trim()
      : String(uploadedAvatar?.pinStoragePath || "").trim(),
});

export const getSponsoredPinCelebrationState = ({
  companyName = "",
  avatarUrl = "",
  pinUrl = "",
  pinGenerationStatus = "idle",
} = {}) => {
  const normalizedCompanyName = String(companyName || "").trim() || "your business";
  const normalizedAvatarUrl = String(avatarUrl || "").trim();
  const normalizedPinUrl = String(pinUrl || "").trim();
  const normalizedStatus = String(pinGenerationStatus || "idle")
    .trim()
    .toLowerCase();

  if (normalizedStatus === "complete" && normalizedPinUrl) {
    return {
      title: "Your branded business icon is ready",
      tone: "success",
      description: `We turned your business logo into the INSSA pin format for ${normalizedCompanyName}.`,
      avatarUrl: normalizedAvatarUrl,
      pinUrl: normalizedPinUrl,
      showPreview: true,
    };
  }

  if (normalizedStatus === "preview_ready" && normalizedPinUrl) {
    return {
      title: "Your branded business icon preview is ready",
      tone: "info",
      description: `This is how ${normalizedCompanyName} will appear in the INSSA pin format when you finish.`,
      avatarUrl: normalizedAvatarUrl,
      pinUrl: normalizedPinUrl,
      showPreview: true,
    };
  }

  if (normalizedPinUrl && normalizedStatus === "idle") {
    return {
      title: "Your branded business icon is ready",
      tone: "success",
      description: `We turned your business logo into the INSSA pin format for ${normalizedCompanyName}.`,
      avatarUrl: normalizedAvatarUrl,
      pinUrl: normalizedPinUrl,
      showPreview: true,
    };
  }

  if (
    normalizedStatus === "loading_template" ||
    normalizedStatus === "generating" ||
    normalizedStatus === "uploading" ||
    normalizedStatus === "preview_loading"
  ) {
    return {
      title: "Creating your branded business icon",
      tone: "info",
      description: "We are applying the live INSSA template to your logo now.",
      avatarUrl: normalizedAvatarUrl,
      pinUrl: normalizedPinUrl,
      showPreview: false,
    };
  }

  if (normalizedStatus === "skipped" || normalizedStatus === "error") {
    return {
      title: "Your business logo is saved",
      tone: "warning",
      description:
        "We could not generate the branded INSSA icon yet, but your business logo upload still completed.",
      avatarUrl: normalizedAvatarUrl,
      pinUrl: normalizedPinUrl,
      showPreview: false,
    };
  }

  return {
    title: "Review your business profile",
    tone: "info",
    description: "We will create your branded INSSA icon as soon as you finish this step.",
    avatarUrl: normalizedAvatarUrl,
    pinUrl: normalizedPinUrl,
    showPreview: false,
  };
};

export const getBusinessWizardFrameConfig = ({ isMobile }) => ({
  stepperOrientation: isMobile ? "horizontal" : "vertical",
  allowStepClick: true,
  allowStepClickForward: true,
});

export const toServiceInput = (data) => {
  // The unified locationEntries list is the source of truth; derive the legacy
  // hq*/locations/locationDetails fields from it so storage stays consistent.
  const locationEntries = sanitizeLocationEntries(data.locationEntries);
  const legacy = locationEntries.length
    ? deriveLegacyLocationFields(locationEntries)
    : {
        hqAddress: data.hqAddress,
        hqPlaceId: data.hqPlaceId || null,
        hqAddressComponents: Array.isArray(data.hqAddressComponents)
          ? data.hqAddressComponents
          : [],
        hqLocation: data.hqLocation || null,
        locations: data.locations,
        locationDetails: data.locationDetails,
      };
  return {
  companyName: data.companyName,
  tagline: String(data.tagline || "").trim(),
  corporationId: data.corporationId,
  businessCategories: Array.isArray(data.businessCategories) ? data.businessCategories : [],
  hqAddress: legacy.hqAddress,
  hqPlaceId: legacy.hqPlaceId || null,
  hqAddressComponents: Array.isArray(legacy.hqAddressComponents)
    ? legacy.hqAddressComponents
    : [],
  hqLocation: legacy.hqLocation || null,
  locations: sanitizeBusinessLocations(legacy.locations, legacy.hqAddress),
  locationDetails: sanitizeBusinessLocationDetails(
    legacy.locationDetails,
    legacy.hqAddress,
    legacy.locations,
  ),
  locationEntries,
  approxEmployeeCount: data.approxEmployeeCount,
  hashtags: Array.isArray(data.hashtags) ? data.hashtags : [],
  websiteUrl: normalizeBusinessWebsiteUrl(data.websiteUrl),
  marketingCampaignDescription: data.marketingCampaignDescription,
  adRestrictions: {
    enabled: Boolean(data.adRestrictionsEnabled),
    categories: Array.isArray(data.adRestrictionCategories)
      ? data.adRestrictionCategories
      : [],
    blockedKeywords: Array.isArray(data.adBlockedKeywords) ? data.adBlockedKeywords : [],
  },
  avatarUrl: String(data.avatarUrl || "").trim() || null,
  avatarStoragePath: String(data.avatarStoragePath || "").trim() || null,
  pinUrl: String(data.pinUrl || "").trim() || null,
  pinStoragePath: String(data.pinStoragePath || "").trim() || null,
  };
};

export const normalizeAddressComponents = (components) => {
  if (!Array.isArray(components)) return [];
  return components.map((c) => ({
    longName: c?.long_name || "",
    shortName: c?.short_name || "",
    types: Array.isArray(c?.types) ? c.types : [],
  }));
};

const getGooglePlacesThemedCss = (mode) => {
  const isDark = mode === "dark";
  const bg = isDark ? "#18181c" : "#ffffff";
  const border = isDark ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.12)";
  const itemBorder = isDark ? "rgba(255, 255, 255, 0.06)" : "rgba(0, 0, 0, 0.08)";
  const text = isDark ? "#f5f5f7" : "#1f2430";
  const query = isDark ? "#ffffff" : "#111111";
  const matched = isDark ? "#a873ff" : "#6c2eff";
  const hover = isDark ? "rgba(168, 115, 255, 0.16)" : "rgba(108, 46, 255, 0.1)";
  const shadow = isDark
    ? "0 16px 36px rgba(0, 0, 0, 0.45)"
    : "0 16px 36px rgba(0, 0, 0, 0.15)";

  return `
    .pac-container {
      margin-top: 6px !important;
      border-radius: 12px !important;
      border: 1px solid ${border} !important;
      background: ${bg} !important;
      box-shadow: ${shadow} !important;
      color: ${text} !important;
      z-index: 1600 !important;
      overflow: hidden !important;
      font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif !important;
    }
    .pac-item {
      padding: 10px 14px !important;
      border-top: 1px solid ${itemBorder} !important;
      color: ${text} !important;
      background: ${bg} !important;
      cursor: pointer !important;
    }
    .pac-item:first-child {
      border-top: none !important;
    }
    .pac-item-query {
      color: ${query} !important;
    }
    .pac-matched {
      color: ${matched} !important;
    }
    .pac-item:hover,
    .pac-item-selected {
      background: ${hover} !important;
    }
  `;
};

export const applyGooglePlacesThemedStyles = (mode) => {
  if (typeof document === "undefined") return;
  let styleTag = document.getElementById(GOOGLE_PLACES_THEME_STYLE_ID);
  if (!styleTag) {
    styleTag = document.createElement("style");
    styleTag.id = GOOGLE_PLACES_THEME_STYLE_ID;
    document.head.appendChild(styleTag);
  }
  styleTag.textContent = getGooglePlacesThemedCss(mode);
};

export const toTokenParts = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .split(" ")
    .map((v) => v.trim())
    .filter(Boolean);

export const buildBusinessSearchTokensFallback = (business = {}) => {
  const seedValues = [
    business.companyName,
    business.tagline,
    business.corporationId,
    business.hqAddress,
    ...(Array.isArray(business.businessCategories) ? business.businessCategories : []),
    ...(Array.isArray(business.locations) ? business.locations : []),
    ...(Array.isArray(business.hashtags) ? business.hashtags : []),
  ];
  const out = new Set();
  seedValues.forEach((value) => toTokenParts(value).forEach((token) => out.add(token)));
  return Array.from(out);
};

export const toMillis = (value) => {
  if (!value) return 0;
  if (typeof value?.toMillis === "function") return value.toMillis();
  if (typeof value?.seconds === "number") return value.seconds * 1000;
  const parsed = Date.parse(String(value));
  return Number.isFinite(parsed) ? parsed : 0;
};

export const formatDateValue = (value) => {
  const ms = toMillis(value);
  if (!ms) return "—";
  try {
    return new Date(ms).toLocaleString();
  } catch {
    return "—";
  }
};

export const formatRelativeDateValue = (value) => {
  const ms = toMillis(value);
  if (!ms) return "—";
  const diffMs = ms - Date.now();
  const abs = Math.abs(diffMs);
  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  const week = 7 * day;

  if (abs < minute) return "just now";
  if (abs < hour) return rtf.format(Math.round(diffMs / minute), "minute");
  if (abs < day) return rtf.format(Math.round(diffMs / hour), "hour");
  if (abs < week) return rtf.format(Math.round(diffMs / day), "day");

  try {
    return new Date(ms).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "—";
  }
};

export const loadImageFromDataUrl = (dataUrl) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Unable to load image."));
    img.src = dataUrl;
  });

export const loadImageFromSrc = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    if (String(src || "").startsWith("http")) {
      img.crossOrigin = "anonymous";
    }
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Unable to load image."));
    img.src = src;
  });

export const createAvatarWebpBlob = async ({
  sourceDataUrl,
  zoom = 1,
  offsetX = 0,
  offsetY = 0,
  size = 512,
  quality = 0.82,
} = {}) => {
  if (!sourceDataUrl) throw new Error("Please choose an image first.");

  const image = await loadImageFromDataUrl(sourceDataUrl);
  const computeRect = (imgW, imgH, frameSize, z, x, y) => {
    const safeZoom = normalizeAvatarCropZoom(z);
    const normalizedOffsetX = Math.max(-100, Math.min(100, Number(x) || 0));
    const normalizedOffsetY = Math.max(-100, Math.min(100, Number(y) || 0));
    const baseScale = Math.max(frameSize / imgW, frameSize / imgH);
    const drawWidth = imgW * baseScale * safeZoom;
    const drawHeight = imgH * baseScale * safeZoom;
    const maxOffsetX = Math.max(0, (drawWidth - frameSize) / 2);
    const maxOffsetY = Math.max(0, (drawHeight - frameSize) / 2);
    const drawX = (frameSize - drawWidth) / 2 + (normalizedOffsetX / 100) * maxOffsetX;
    const drawY = (frameSize - drawHeight) / 2 + (normalizedOffsetY / 100) * maxOffsetY;
    return { drawX, drawY, drawWidth, drawHeight };
  };
  const safeZoom = normalizeAvatarCropZoom(zoom);
  const normalizedOffsetX = Math.max(-100, Math.min(100, Number(offsetX) || 0));
  const normalizedOffsetY = Math.max(-100, Math.min(100, Number(offsetY) || 0));

  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not process avatar image.");

  const { drawX, drawY, drawWidth, drawHeight } = computeRect(
    image.naturalWidth,
    image.naturalHeight,
    size,
    safeZoom,
    normalizedOffsetX,
    normalizedOffsetY
  );

  ctx.clearRect(0, 0, size, size);
  ctx.save();
  ctx.beginPath();
  ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
  ctx.closePath();
  ctx.clip();
  ctx.drawImage(image, drawX, drawY, drawWidth, drawHeight);
  ctx.restore();

  const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/webp", quality));
  if (!blob) throw new Error("Could not convert avatar to WebP.");
  return blob;
};

export const createSponsoredPinWebpBlob = async ({
  sourceDataUrl,
  template,
  zoom = 1,
  offsetX = 0,
  offsetY = 0,
  outputSize = 200,
} = {}) => {
  if (!sourceDataUrl) throw new Error("Please choose an image first.");
  const templateSource =
    String(template?.templateImageDataUrl || "").trim() ||
    String(template?.templateImageUrl || "").trim();
  if (!templateSource || !template?.preset) {
    throw new Error("No active sponsored pin template is configured.");
  }

  const templatePreset = template.preset || {};
  const templateConfig = templatePreset.template || {};
  const circleConfig = templatePreset.circleCrop || {};
  const fitConfig = templatePreset.userPhotoFit || {};
  const outputConfig = templatePreset.output || {};

  const templateWidth = Math.max(1, Number(templateConfig.width) || 550);
  const templateHeight = Math.max(1, Number(templateConfig.height) || 550);
  const circleDiameter = Math.max(1, Number(circleConfig.diameter) || 228);
  const circleCenterX = Number(circleConfig.centerX) || Math.round(templateWidth / 2);
  const circleCenterY = Number(circleConfig.centerY) || Math.round(templateHeight / 2);
  const combinedZoom = normalizeAvatarCropZoom(
    (Number(fitConfig.zoom) || 1) * (Number(zoom) || 1)
  );
  const normalizedOffsetX = Math.max(-100, Math.min(100, Number(offsetX) || 0));
  const normalizedOffsetY = Math.max(-100, Math.min(100, Number(offsetY) || 0));
  const presetOffsetX = Number(fitConfig.offsetX) || 0;
  const presetOffsetY = Number(fitConfig.offsetY) || 0;
  const webpQuality = Math.max(0.1, Math.min(1, Number(outputConfig.quality) || 0.92));

  const [userImage, templateImage] = await Promise.all([
    loadImageFromSrc(sourceDataUrl),
    loadImageFromSrc(templateSource),
  ]);

  const workingCanvas = document.createElement("canvas");
  workingCanvas.width = templateWidth;
  workingCanvas.height = templateHeight;
  const workingCtx = workingCanvas.getContext("2d");
  if (!workingCtx) throw new Error("Could not process sponsored pin image.");

  const baseScale = Math.max(circleDiameter / userImage.naturalWidth, circleDiameter / userImage.naturalHeight);
  const drawWidth = userImage.naturalWidth * baseScale * combinedZoom;
  const drawHeight = userImage.naturalHeight * baseScale * combinedZoom;
  const maxOffsetX = Math.max(0, (drawWidth - circleDiameter) / 2);
  const maxOffsetY = Math.max(0, (drawHeight - circleDiameter) / 2);
  const userOffsetPxX = (normalizedOffsetX / 100) * maxOffsetX;
  const userOffsetPxY = (normalizedOffsetY / 100) * maxOffsetY;

  workingCtx.clearRect(0, 0, templateWidth, templateHeight);
  workingCtx.drawImage(templateImage, 0, 0, templateWidth, templateHeight);

  workingCtx.save();
  workingCtx.beginPath();
  workingCtx.arc(circleCenterX, circleCenterY, circleDiameter / 2, 0, Math.PI * 2);
  workingCtx.closePath();
  workingCtx.clip();
  workingCtx.drawImage(
    userImage,
    circleCenterX - drawWidth / 2 + presetOffsetX + userOffsetPxX,
    circleCenterY - drawHeight / 2 + presetOffsetY + userOffsetPxY,
    drawWidth,
    drawHeight
  );
  workingCtx.restore();

  const outputCanvas = document.createElement("canvas");
  outputCanvas.width = Math.max(1, Number(outputSize) || 200);
  outputCanvas.height = Math.max(1, Number(outputSize) || 200);
  const outputCtx = outputCanvas.getContext("2d");
  if (!outputCtx) throw new Error("Could not finalize sponsored pin image.");

  // Keep canvas transparent by only clearing and drawing source layers; never fill a background.
  outputCtx.clearRect(0, 0, outputCanvas.width, outputCanvas.height);
  outputCtx.drawImage(workingCanvas, 0, 0, outputCanvas.width, outputCanvas.height);

  const blob = await new Promise((resolve) => outputCanvas.toBlob(resolve, "image/webp", webpQuality));
  if (!blob) throw new Error("Could not convert sponsored pin to WebP.");
  return blob;
};

export const getAvatarPreviewRect = ({
  sourceWidth = 0,
  sourceHeight = 0,
  frameSize = 220,
  zoom = 1,
  offsetX = 0,
  offsetY = 0,
} = {}) => {
  const imgW = Math.max(1, Number(sourceWidth) || frameSize);
  const imgH = Math.max(1, Number(sourceHeight) || frameSize);
  const safeZoom = normalizeAvatarCropZoom(zoom);
  const normalizedOffsetX = Math.max(-100, Math.min(100, Number(offsetX) || 0));
  const normalizedOffsetY = Math.max(-100, Math.min(100, Number(offsetY) || 0));
  const baseScale = Math.max(frameSize / imgW, frameSize / imgH);
  const drawWidth = imgW * baseScale * safeZoom;
  const drawHeight = imgH * baseScale * safeZoom;
  const maxOffsetX = Math.max(0, (drawWidth - frameSize) / 2);
  const maxOffsetY = Math.max(0, (drawHeight - frameSize) / 2);
  const drawX = (frameSize - drawWidth) / 2 + (normalizedOffsetX / 100) * maxOffsetX;
  const drawY = (frameSize - drawHeight) / 2 + (normalizedOffsetY / 100) * maxOffsetY;
  return { drawX, drawY, drawWidth, drawHeight };
};
